import {
  addProgramDay,
  addProgramWeek,
  archiveProgram,
  copyWeekWithReps,
  duplicateProgram,
  findExercise,
  getProgram,
  listExercises,
  removeLastProgramWeek,
  removeProgramDay,
  renameProgramDay,
  saveDay,
  setActive,
  updateProgramMeta,
  type ExerciseRow,
  type ProgramDetail,
} from '@gymtv/data';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DayGrid } from '../components/DayGrid';
import { useAuth } from '../lib/auth';
import {
  addRow,
  daysFromProgram,
  daysOfWeek,
  moveRow,
  navigate,
  overallStatus,
  progression,
  removeRow,
  updateCell,
  validateDay,
  type Col,
  type DayState,
} from '../lib/editor';
import { supabase } from '../lib/supabase';

const AUTOSAVE_MS = 700;

export function ProgramEditor() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const { units } = useAuth();
  const [program, setProgram] = useState<ProgramDetail | null>(null);
  const [days, setDays] = useState<Record<string, DayState>>({});
  const [week, setWeek] = useState(1);
  const [view, setView] = useState<'week' | 'progression'>('week');
  const [position, setPosition] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [exercises, setExercises] = useState<ExerciseRow[]>([]);
  const daysRef = useRef(days);
  daysRef.current = days;
  const exercisesRef = useRef(exercises);
  exercisesRef.current = exercises;

  const refreshLibrary = useCallback(() => {
    listExercises(supabase).then(setExercises, () => {});
  }, []);

  const load = useCallback(async () => {
    refreshLibrary();
    const p = await getProgram(supabase, id);
    setProgram(p);
    setDays(daysFromProgram(p));
    setWeek((w) => Math.min(w, p.weeks));
  }, [id, refreshLibrary]);

  useEffect(() => {
    load().catch((e) => setError(e.message));
  }, [load]);

  // ── autosave: each dirty day is validated and saved after a short pause in typing ──
  const saveOne = useCallback(async (dayId: string) => {
    const d = daysRef.current[dayId];
    if (!d) return;
    const v = validateDay(d);
    if (!v.ok) {
      setDays((s) => ({ ...s, [dayId]: { ...s[dayId]!, status: 'invalid', error: v.error, badRow: v.row } }));
      return;
    }
    const rev = d.rev;
    setDays((s) => ({ ...s, [dayId]: { ...s[dayId]!, status: 'saving', error: null, badRow: null } }));
    try {
      await saveDay(supabase, dayId, d.name, v.exercises);
      // New names were just added to the library (and spellings matched to it).
      if (v.exercises.some((e) => !findExercise(exercisesRef.current, e.name))) refreshLibrary();
      setDays((s) => {
        const cur = s[dayId]!;
        return { ...s, [dayId]: { ...cur, savedRev: rev, status: cur.rev === rev ? 'saved' : 'dirty' } };
      });
    } catch (e: any) {
      setDays((s) => ({ ...s, [dayId]: { ...s[dayId]!, status: 'error', error: `Not saved: ${e.message}` } }));
    }
  }, [refreshLibrary]);

  const pending = Object.values(days)
    .filter((d) => d.status === 'dirty')
    .map((d) => `${d.id}:${d.rev}`)
    .join(',');
  useEffect(() => {
    if (!pending) return;
    const t = setTimeout(() => {
      for (const d of Object.values(daysRef.current)) if (d.status === 'dirty') saveOne(d.id);
    }, AUTOSAVE_MS);
    return () => clearTimeout(t);
  }, [pending, saveOne]);

  /** Saves everything outstanding before a structural change reloads the program. */
  async function flush(): Promise<boolean> {
    const dirty = Object.values(daysRef.current).filter((d) => d.status !== 'saved');
    for (const d of dirty) await saveOne(d.id);
    const bad = Object.values(daysRef.current).find((d) => d.status === 'invalid' || d.status === 'error');
    if (bad) {
      setError(`Fix week ${bad.week} “${bad.name}” first: ${bad.error}`);
      return false;
    }
    return true;
  }

  async function structural(fn: () => Promise<unknown>, after?: () => void) {
    setError(null);
    if (!(await flush())) return;
    setBusy(true);
    try {
      await fn();
      await load();
      after?.();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  // warn before leaving with unsaved edits
  const status = overallStatus(days);
  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => {
      if (status !== 'saved') e.preventDefault();
    };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [status]);

  const set = (dayId: string, f: (d: DayState) => DayState) => setDays((s) => ({ ...s, [dayId]: f(s[dayId]!) }));

  if (!program) return error ? <p className="error">{error}</p> : <p className="muted">Loading…</p>;
  const weekDays = daysOfWeek(days, week);
  const positions = daysOfWeek(days, 1);

  return (
    <>
      <div className="page-head">
        <div className="stack" style={{ gap: 6, minWidth: 0 }}>
          <ProgramName program={program} onSave={(name) => updateProgramMeta(supabase, program.id, { name }).then(load, (e) => setError(e.message))} />
          <p className="muted">
            {program.weeks} {program.weeks === 1 ? 'week' : 'weeks'} · {positions.length} {positions.length === 1 ? 'day' : 'days'} per week ·{' '}
            {program.is_active ? <span className="pill ok">active</span> : <span>not active</span>} ·{' '}
            {status === 'saved' ? 'all changes saved' : status === 'saving' || status === 'dirty' ? 'saving…' : 'some changes not saved'}
          </p>
        </div>
        <div className="actions">
          {!program.is_active && (
            <button onClick={() => structural(() => setActive(supabase, program.id))} disabled={busy}>
              Make active
            </button>
          )}
          <button
            onClick={() =>
              structural(async () => {
                const copy = await duplicateProgram(supabase, program.id);
                nav(`/programs/${copy.id}`);
              })
            }
            disabled={busy}
          >
            Duplicate
          </button>
          <button
            className="danger"
            onClick={() => {
              if (window.confirm(`Archive “${program.name}”? Past sessions are kept.`)) structural(() => archiveProgram(supabase, program.id), () => nav('/programs'));
            }}
            disabled={busy}
          >
            Archive
          </button>
        </div>
      </div>

      {error && <p className="error" role="alert">{error}</p>}

      <div className="row" style={{ justifyContent: 'space-between' }}>
        <div className="tabs" role="group" aria-label="View">
          <button aria-pressed={view === 'week'} onClick={() => setView('week')}>Week view</button>
          <button aria-pressed={view === 'progression'} onClick={() => setView('progression')}>Across weeks</button>
        </div>
        {view === 'week' ? (
          <div className="tabs" role="group" aria-label="Week">
            {Array.from({ length: program.weeks }, (_, i) => i + 1).map((w) => (
              <button key={w} aria-pressed={w === week} onClick={() => setWeek(w)}>
                Week {w}
              </button>
            ))}
          </div>
        ) : (
          <div className="tabs" role="group" aria-label="Day">
            {positions.map((d) => (
              <button key={d.position} aria-pressed={d.position === position} onClick={() => setPosition(d.position)}>
                {d.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {view === 'week' ? (
        <div className="days">
          {weekDays.map((d) => (
            <DayGrid
              key={d.id}
              day={d}
              units={units}
              exercises={exercises}
              navigate={navigate}
              onCell={(row, col: Col, value) => set(d.id, (x) => updateCell(x, row, col, value))}
              onAddRow={(at) => set(d.id, (x) => addRow(x, at, x.rows[at - 1]))}
              onRemoveRow={(row) => set(d.id, (x) => removeRow(x, row))}
              onMoveRow={(from, to) => set(d.id, (x) => moveRow(x, from, to))}
              onRename={(name) => structural(() => renameProgramDay(supabase, program.id, d.position, name))}
              onRemoveDay={
                positions.length > 1
                  ? () => {
                      if (window.confirm(`Remove “${d.name}” from every week?`)) structural(() => removeProgramDay(supabase, program.id, d.position));
                    }
                  : undefined
              }
            />
          ))}
        </div>
      ) : (
        <Progression
          days={days}
          position={position}
          weeks={program.weeks}
          units={units}
          onCell={(dayId, row, col, value) => set(dayId, (x) => updateCell(x, row, col, value))}
        />
      )}

      <WeekTools
        weeks={program.weeks}
        week={week}
        units={units}
        busy={busy}
        onCopy={(from, to, wd, rd) => structural(() => copyWeekWithReps(supabase, program.id, from, to, wd, rd), () => setWeek(to))}
        onFill={(from, wd, rd) =>
          structural(async () => {
            for (let w = from + 1; w <= program.weeks; w++) await copyWeekWithReps(supabase, program.id, w - 1, w, wd, rd);
          })
        }
        onAddWeek={(wd, rd) => structural(() => addProgramWeek(supabase, program.id, wd, rd), () => setWeek(program.weeks + 1))}
        onRemoveWeek={() => {
          if (window.confirm(`Delete week ${program.weeks} and everything in it?`))
            structural(() => removeLastProgramWeek(supabase, program.id), () => setWeek((w) => Math.min(w, program.weeks - 1)));
        }}
        onAddDay={(name) => structural(() => addProgramDay(supabase, program.id, name))}
      />
    </>
  );
}

function ProgramName({ program, onSave }: { program: ProgramDetail; onSave(name: string): void }) {
  const [name, setName] = useState(program.name);
  useEffect(() => setName(program.name), [program.name]);
  return (
    <input
      aria-label="Program name"
      value={name}
      onChange={(e) => setName(e.target.value)}
      onBlur={() => name.trim() && name.trim() !== program.name && onSave(name.trim())}
      onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
      style={{ fontFamily: 'var(--display)', fontWeight: 700, fontSize: '2.2rem', border: '1px solid transparent', background: 'transparent', padding: '0 6px', marginLeft: -6, maxWidth: '100%' }}
    />
  );
}

function Progression({
  days,
  position,
  weeks,
  units,
  onCell,
}: {
  days: Record<string, DayState>;
  position: number;
  weeks: number;
  units: string;
  onCell(dayId: string, row: number, col: Col, value: string): void;
}) {
  const rows = progression(days, position, weeks);
  if (!rows.length) return <p className="muted">This day has no exercises yet. Add them in the week view.</p>;
  return (
    <div className="table-wrap">
      <table className="prog">
        <thead>
          <tr>
            <th>Exercise</th>
            {Array.from({ length: weeks }, (_, i) => (
              <th key={i}>Week {i + 1}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="ex">{r.label}</td>
              {r.cells.map((c, w) =>
                c ? (
                  <td key={w}>
                    <span className="cell3">
                      <input aria-label={`${r.label} week ${w + 1} sets`} inputMode="numeric" value={c.draft.sets} onChange={(e) => onCell(c.dayId, c.row, 'sets', e.target.value)} />
                      <span>×</span>
                      <input aria-label={`${r.label} week ${w + 1} reps`} inputMode="numeric" value={c.draft.reps} onChange={(e) => onCell(c.dayId, c.row, 'reps', e.target.value)} />
                      <span>@</span>
                      <input className="w" aria-label={`${r.label} week ${w + 1} weight`} inputMode="decimal" value={c.draft.weight} onChange={(e) => onCell(c.dayId, c.row, 'weight', e.target.value)} />
                      <span>{units}</span>
                    </span>
                  </td>
                ) : (
                  <td key={w} className="muted">—</td>
                ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function WeekTools(p: {
  weeks: number;
  week: number;
  units: string;
  busy: boolean;
  onCopy(from: number, to: number, weightDelta: number, repsDelta: number): void;
  onFill(from: number, weightDelta: number, repsDelta: number): void;
  onAddWeek(weightDelta: number, repsDelta: number): void;
  onRemoveWeek(): void;
  onAddDay(name: string): void;
}) {
  const [from, setFrom] = useState(p.week);
  const [to, setTo] = useState(Math.min(p.week + 1, p.weeks));
  const [wd, setWd] = useState(p.units === 'lb' ? '5' : '2.5');
  const [rd, setRd] = useState('0');
  const [dayName, setDayName] = useState('');
  useEffect(() => {
    setFrom(p.week);
    setTo(Math.min(p.week + 1, p.weeks));
  }, [p.week, p.weeks]);
  const num = (s: string) => Number(s.replace(',', '.')) || 0;
  const weekOpts = Array.from({ length: p.weeks }, (_, i) => i + 1);
  return (
    <section className="card stack" aria-label="Weeks and days">
      <h2>Weeks and days</h2>
      <div className="row">
        <span>Copy week</span>
        <select aria-label="Copy from week" value={from} onChange={(e) => setFrom(Number(e.target.value))}>
          {weekOpts.map((w) => <option key={w} value={w}>{w}</option>)}
        </select>
        <span>to</span>
        <select aria-label="Copy to week" value={to} onChange={(e) => setTo(Number(e.target.value))}>
          {weekOpts.map((w) => <option key={w} value={w}>{w}</option>)}
        </select>
        <span>adding</span>
        <input aria-label={`Weight change (${p.units})`} inputMode="decimal" value={wd} onChange={(e) => setWd(e.target.value)} style={{ width: '5em' }} />
        <span>{p.units} and</span>
        <input aria-label="Reps change" inputMode="numeric" value={rd} onChange={(e) => setRd(e.target.value)} style={{ width: '4em' }} />
        <span>reps</span>
        <button disabled={p.busy || from === to} onClick={() => p.onCopy(from, to, num(wd), Math.round(num(rd)))}>
          Copy
        </button>
        <button disabled={p.busy || from >= p.weeks} onClick={() => p.onFill(from, num(wd), Math.round(num(rd)))} title="Week by week: each later week = the one before plus the change">
          Fill all later weeks
        </button>
      </div>
      <p className="hint">Copying replaces the target week. “Fill all later weeks” builds a progression: each week is the previous one plus the change.</p>
      <div className="row">
        <button disabled={p.busy || p.weeks >= 52} onClick={() => p.onAddWeek(num(wd), Math.round(num(rd)))}>
          + Add week {p.weeks + 1} (copy of week {p.weeks} plus the change)
        </button>
        <button className="danger" disabled={p.busy || p.weeks <= 1} onClick={p.onRemoveWeek}>
          Delete week {p.weeks}
        </button>
      </div>
      <div className="row">
        <input aria-label="New day name" placeholder="New day name, e.g. Day D" value={dayName} onChange={(e) => setDayName(e.target.value)} />
        <button
          disabled={p.busy}
          onClick={() => {
            p.onAddDay(dayName.trim());
            setDayName('');
          }}
        >
          + Add day to every week
        </button>
      </div>
    </section>
  );
}

